// utils/notifications.js
import notifee, {
  AndroidImportance,
  RepeatFrequency,
  TriggerType,
} from "@notifee/react-native";
import DeviceInfo from "react-native-device-info";
import { NOTIFICATION_ACTION_FAIL, NOTIFICATION_ACTION_SUCCESS } from "./notificationCheckIn.js";

const CHANNEL_DAILY = "daily-checkin";
const CHANNEL_LIMITS = "app-limits";
const CHANNEL_LOCKBOX = "lockbox";

export const DAILY_MORNING_ID = "daily-checkin-morning";
export const DAILY_EVENING_ID = "daily-checkin-evening";

function nextDailyTimestamp(hour, minute) {
  const next = new Date();
  next.setHours(hour, minute, 0, 0);
  if (next.getTime() <= Date.now()) {
    next.setDate(next.getDate() + 1);
  }
  return next.getTime();
}

function dailyCheckInNotificationContent(title, body) {
  return {
    title,
    body,
    android: {
      channelId: CHANNEL_DAILY,
      pressAction: { id: "default" },
      actions: [
        {
          title: "Mark today as success",
          pressAction: { id: NOTIFICATION_ACTION_SUCCESS },
        },
        {
          title: "Mark fail",
          pressAction: { id: NOTIFICATION_ACTION_FAIL },
        },
      ],
    },
    ios: {
      categoryId: "DAILY_CHECKIN",
    },
  };
}

export async function setupNotificationActions() {
  try {
    await notifee.createChannel({
      id: CHANNEL_DAILY,
      name: "Daily check-in",
      importance: AndroidImportance.HIGH,
    });
    await notifee.createChannel({
      id: CHANNEL_LIMITS,
      name: "App time limits",
      importance: AndroidImportance.HIGH,
    });
    await notifee.createChannel({
      id: CHANNEL_LOCKBOX,
      name: "Lockbox",
      importance: AndroidImportance.HIGH,
    });

    await notifee.setNotificationCategories([
      {
        id: "DAILY_CHECKIN",
        actions: [
          {
            id: NOTIFICATION_ACTION_SUCCESS,
            title: "Mark today as success",
            foreground: false,
          },
          {
            id: NOTIFICATION_ACTION_FAIL,
            title: "Mark fail",
            foreground: false,
            destructive: true,
          },
        ],
      },
    ]);
  } catch (e) {
    console.warn("Notification category error:", e);
  }
}

export async function registerForPushNotifications() {
  const isEmulator = await DeviceInfo.isEmulator();
  if (isEmulator) {
    console.warn("Notifications require a real device.");
    return false;
  }

  const settings = await notifee.requestPermission();
  const granted =
    settings.authorizationStatus === 1 || settings.authorizationStatus === 2;

  if (!granted) {
    alert("Grant notification permissions to use Procsystim check-in reminders.");
    return false;
  }

  await setupNotificationActions();
  return true;
}

export async function cancelDailyCheckInNotifications() {
  await notifee.cancelNotification(DAILY_MORNING_ID).catch(() => {});
  await notifee.cancelNotification(DAILY_EVENING_ID).catch(() => {});
}

export async function scheduleDailyNotifications() {
  await cancelDailyCheckInNotifications();

  await notifee.createTriggerNotification(
    {
      id: DAILY_MORNING_ID,
      ...dailyCheckInNotificationContent(
        "Procsystim",
        "How did today go? Mark from here — no need to open the app.",
      ),
    },
    {
      type: TriggerType.TIMESTAMP,
      timestamp: nextDailyTimestamp(8, 0),
      repeatFrequency: RepeatFrequency.DAILY,
    },
  );

  await notifee.createTriggerNotification(
    {
      id: DAILY_EVENING_ID,
      ...dailyCheckInNotificationContent(
        "Procsystim check-in",
        "Quick tap: mark today as success or fail.",
      ),
    },
    {
      type: TriggerType.TIMESTAMP,
      timestamp: nextDailyTimestamp(20, 0),
      repeatFrequency: RepeatFrequency.DAILY,
    },
  );
}

/**
 * Turn daily check-in reminders on or off (8:00 and 20:00 with success/fail buttons).
 *
 * @param {boolean} enabled
 * @returns {Promise<boolean>} whether reminders are active
 */
export async function syncDailyCheckInNotifications(enabled) {
  if (!enabled) {
    await cancelDailyCheckInNotifications();
    return false;
  }
  const permitted = await registerForPushNotifications();
  if (!permitted) {
    await cancelDailyCheckInNotifications();
    return false;
  }
  await scheduleDailyNotifications();
  return true;
}

/** Reminder only — cannot force-close other apps (OS limitation). Min 1 minute. */
export async function scheduleAppLimitNotification(label, minutes) {
  const mins = Math.max(1, Math.floor(Number(minutes) || 0));
  const seconds = Math.min(86400, mins * 60);
  const id = `app-limit-${Date.now()}`;
  await notifee.createTriggerNotification(
    {
      id,
      title: "App time limit",
      body: `Time is up for: ${label || "your session"}.`,
      android: { channelId: CHANNEL_LIMITS },
    },
    {
      type: TriggerType.TIMESTAMP,
      timestamp: Date.now() + seconds * 1000,
    },
  );
  return id;
}

/** Immediate local notification (e.g. Lockbox auto-block). */
export async function displayLocalNotification(title, body) {
  await setupNotificationActions();
  await notifee.displayNotification({
    title,
    body,
    android: { channelId: CHANNEL_LOCKBOX },
  });
}
